import { useCallback, useState } from "react";

import type { ImpersonationSession } from "../types";
import {
  getDefaultWindowSize,
  getInitialWindowPosition,
  nextZIndex,
} from "../utils/sessionHelpers";

const TAB_GROUP_WIDTH_RATIO = 0.6;
const TAB_GROUP_HEIGHT_RATIO = 0.6;

function getDefaultTabGroupSize(): { width: number; height: number } {
  return {
    width: Math.round(window.innerWidth * TAB_GROUP_WIDTH_RATIO),
    height: Math.round(window.innerHeight * TAB_GROUP_HEIGHT_RATIO),
  };
}

export interface SplitLayout {
  leftSessionId: string;
  rightSessionId: string;
}

export function useTabGroup(
  sessionsRef: React.RefObject<ImpersonationSession[]>,
  setSessions: React.Dispatch<React.SetStateAction<ImpersonationSession[]>>
) {
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isTabGroupMinimized, setIsTabGroupMinimized] = useState(false);
  const [tabGroupPosition, setTabGroupPosition] = useState({ x: 100, y: 100 });
  const [tabGroupSize, setTabGroupSize] = useState(getDefaultTabGroupSize);
  const [splitLayout, setSplitLayout] = useState<SplitLayout | null>(null);
  const [lastActiveSplitSide, setLastActiveSplitSide] = useState<"left" | "right">("right");

  const enterSplitView = useCallback((leftSessionId: string, rightSessionId: string) => {
    setSplitLayout({ leftSessionId, rightSessionId });
    setActiveTabId(null);
    setLastActiveSplitSide("right");
  }, []);

  const exitSplitView = useCallback(() => {
    setSplitLayout((currentSplit) => {
      if (!currentSplit) {
        return null;
      }

      setActiveTabId(
        lastActiveSplitSide === "left" ? currentSplit.leftSessionId : currentSplit.rightSessionId
      );
      return null;
    });
  }, [lastActiveSplitSide]);

  const popOutSession = useCallback(
    (id: string) => {
      // If the popped session is in the split layout, exit split view first
      setSplitLayout((currentSplit) => {
        if (!currentSplit) {
          return null;
        }

        if (currentSplit.leftSessionId === id) {
          setActiveTabId(currentSplit.rightSessionId);
          return null;
        }

        if (currentSplit.rightSessionId === id) {
          setActiveTabId(currentSplit.leftSessionId);
          return null;
        }

        return currentSplit;
      });

      setSessions((previous) => {
        const session = previous.find((s) => s.id === id);
        if (!session) {
          return previous;
        }

        const size = getDefaultWindowSize(session.sessionType);
        const position = getInitialWindowPosition(previous.filter((s) => !s.isDocked).length, size);

        return previous.map((s) =>
          s.id === id ? { ...s, isDocked: false, position, size, zIndex: nextZIndex() } : s
        );
      });

      setActiveTabId((currentActiveTabId) => {
        if (currentActiveTabId !== id) {
          return currentActiveTabId;
        }

        const dockedSessions = (sessionsRef.current ?? []).filter(
          (s) => s.isDocked && s.isActive && s.id !== id
        );
        return dockedSessions.length > 0 ? dockedSessions[0].id : null;
      });
    },
    [sessionsRef, setSessions]
  );

  const setActiveTab = useCallback(
    (id: string | null) => {
      setSplitLayout((currentSplit) => {
        if (!currentSplit || id === null) {
          setActiveTabId(id);
          return currentSplit;
        }

        // If the clicked tab is already in the split, just update the active side
        if (currentSplit.leftSessionId === id) {
          setLastActiveSplitSide("left");
          return currentSplit;
        }

        if (currentSplit.rightSessionId === id) {
          setLastActiveSplitSide("right");
          return currentSplit;
        }

        // Clicked tab is NOT in the split — replace the lastActiveSplitSide session
        if (lastActiveSplitSide === "left") {
          setLastActiveSplitSide("left");
          return { ...currentSplit, leftSessionId: id };
        }

        setLastActiveSplitSide("right");
        return { ...currentSplit, rightSessionId: id };
      });
    },
    [lastActiveSplitSide]
  );

  const dockSession = useCallback(
    (id: string) => {
      setSessions((previous) => previous.map((s) => (s.id === id ? { ...s, isDocked: true } : s)));
      setActiveTabId(id);
    },
    [setSessions]
  );

  const minimizeTabGroup = useCallback(() => {
    setIsTabGroupMinimized(true);
  }, []);

  const restoreTabGroup = useCallback(() => {
    setIsTabGroupMinimized(false);
  }, []);

  const updateTabGroupPosition = useCallback((position: { x: number; y: number }) => {
    setTabGroupPosition(position);
  }, []);

  const updateTabGroupSize = useCallback((size: { width: number; height: number }) => {
    setTabGroupSize(size);
  }, []);

  return {
    activeTabId,
    isTabGroupMinimized,
    tabGroupPosition,
    tabGroupSize,
    splitLayout,
    lastActiveSplitSide,
    setActiveTab,
    popOutSession,
    dockSession,
    minimizeTabGroup,
    restoreTabGroup,
    updateTabGroupPosition,
    updateTabGroupSize,
    enterSplitView,
    exitSplitView,
    setLastActiveSplitSide,
  };
}
